/* eslint-disable max-lines */
import {
  recruitNurseSenkaUrl,
  termsOfUseUrl,
} from '../../../../features/url/baseUrl';
import { generateAuthorizationUrl } from '../../../../features/url/generateAuthorizationUrl';
import { CheckboxAttribute } from '../../../../types/CheckboxAttribute';
import { BlockErrorMessage } from '../../../Block/BlockErrorMessage';
import { ButtonBase } from '../../../Button/ButtonBase';
import { ButtonText } from '../../../Button/ButtonText';
import { CheckBox } from '../../../Form/CheckBox';
import { FormListInputAndText } from '../../../Form/FormList/FormListInputAndText';
import { FormListName } from '../../../Form/FormList/FormListName';
import { FormListPassword } from '../../../Form/FormList/FormListPassword';
import { FromListBirthdate } from '../../../Form/FormList/FromListBirthdate';
import { InputText } from '../../../Form/InputText';
import { InputTextWithSuggestions } from '../../../Form/InputTextWithSuggestions';
import { Select } from '../../../Form/Select';
import { InnerButtonArea } from '../../../Layout/InnerLayout/InnerButtonArea';
import { InnerMargin } from '../../../Layout/InnerLayout/InnerMargin';
import { TableBody } from '../../../Table/TableBody';
import { TableTrFormLabel } from '../../../Table/TableTr/TableTrFromLabel';

import { createInvalidErrors } from './createInvalidErrors';

import type { RegisterContentsProps } from './RegisterContentsProps';
import type { FC, JSX } from 'react';

const EmailNote = (): JSX.Element => (
  <>
    <span>
      @icloud.comのメールアドレスをご利用の場合、当社からのメールが迷惑メールフォルダに振り分けられることがあります。
      <a
        href="https://support.apple.com/ja-jp/guide/icloud/mm6b1a2ced/icloud"
        target="_blank"
        rel="noopener noreferrer"
      >
        詳しくはこちら
      </a>
    </span>
  </>
);

// eslint-disable-next-line max-lines-per-function
export const RegisterContents: FC<RegisterContentsProps> = ({
  appEnv,
  qualificationAttributes,
  graduationYearAttributes,
  prefectureAttributes,
  schoolTypeAttributes,
  birthYearAttributes,
  birthMonthAttributes,
  birthdayAttributes,
  namePlaceholders,
  isStudent,
  isReceiveHospitalNotices,
  isReceiveHospitalNoticesLabelText,
  invalidParams,
  disableSubmitButton,
  errorMessage,
  selectedBirthYear,
  authorizationRequest,
  formFields,
  eventHandlers,
}) => {
  const invalidErrors = createInvalidErrors(invalidParams);

  const isReceiveHospitalNoticesAttribute: CheckboxAttribute[] = [
    {
      id: 'isReceiveHospitalNotices',
      name: 'isReceiveHospitalNotices',
      value: 1,
      label: isReceiveHospitalNoticesLabelText,
      checked: isReceiveHospitalNotices,
    },
  ];

  return (
    <>
      <InnerMargin align="default">
        本サイトは、医療従事者の方限定です。既にアカウントをお持ちの方は
        <ButtonText
          color="default"
          useNextLink={true}
          link={generateAuthorizationUrl(appEnv, authorizationRequest)}
        >
          こちら
        </ButtonText>
      </InnerMargin>
      <form method="post" onSubmit={eventHandlers?.submit}>
        <TableBody>
          <TableTrFormLabel
            id="qualification_1"
            required={true}
            multiple={true}
            errorMessage={
              invalidErrors?.qualificationCodeListError?.reason
                ? invalidErrors?.qualificationCodeListError?.reason
                : ''
            }
            title="保有資格"
          >
            <CheckBox
              columns="three"
              attributes={qualificationAttributes}
              onChange={eventHandlers?.changeQualificationsCheckBox}
            />
          </TableTrFormLabel>
          {isStudent ? (
            <>
              <TableTrFormLabel
                id="graduationYear"
                required={true}
                errorMessage={
                  invalidErrors?.graduationYearError?.reason
                    ? invalidErrors?.graduationYearError?.reason
                    : ''
                }
                title="卒業予定年"
              >
                <Select
                  id="graduationYear"
                  width="default"
                  attributes={graduationYearAttributes}
                  onChange={eventHandlers?.changeGraduationYear}
                />
              </TableTrFormLabel>
              <TableTrFormLabel
                id="schoolName"
                required={true}
                errorMessage={
                  invalidErrors?.schoolNameError?.reason
                    ? invalidErrors?.schoolNameError?.reason
                    : ''
                }
                title="卒業済み/卒業予定の学校名"
              >
                <FormListInputAndText text="※頭文字を入力すると該当する学校名が表示されます">
                  <InputTextWithSuggestions
                    name="schoolName"
                    id="schoolName"
                    type="text"
                    width="default"
                    placeholder="学校名を入力してください"
                    value={formFields?.schoolName}
                    onChange={eventHandlers?.changeSchoolName}
                    fetchSuggestions={eventHandlers?.fetchSchoolNameSuggestions}
                    afterClickSuggestionText={
                      eventHandlers?.afterClickSchoolNameSuggestionText
                    }
                  />
                </FormListInputAndText>
              </TableTrFormLabel>
              <TableTrFormLabel
                id="schoolType"
                required={true}
                errorMessage={
                  invalidErrors?.schoolTypeIdError?.reason
                    ? invalidErrors?.schoolTypeIdError?.reason
                    : ''
                }
                title="学校種別"
              >
                <Select
                  id="schoolType"
                  width="default"
                  attributes={schoolTypeAttributes}
                  onChange={eventHandlers?.changeSchoolType}
                />
              </TableTrFormLabel>
            </>
          ) : (
            ''
          )}

          <TableTrFormLabel
            id="birthYear"
            required={true}
            errorMessage={
              invalidErrors?.birthdateError?.reason
                ? invalidErrors?.birthdateError?.reason
                : ''
            }
            title="生年月日"
          >
            <FromListBirthdate
              birthYearAttributes={birthYearAttributes}
              birthMonthAttributes={birthMonthAttributes}
              birthdayAttributes={birthdayAttributes}
              selectedBirthYear={selectedBirthYear}
              handleChangeBirthYear={eventHandlers?.changeBirthYear}
              handleChangeBirthMonth={eventHandlers?.changeBirthMonth}
              handleChangeBirthday={eventHandlers?.changeBirthday}
            />
          </TableTrFormLabel>
          <TableTrFormLabel
            id="prefecture"
            required={true}
            errorMessage={
              invalidErrors?.prefectureIdError?.reason
                ? invalidErrors?.prefectureIdError?.reason
                : ''
            }
            title="都道府県"
          >
            <Select
              id="prefecture"
              width="default"
              attributes={prefectureAttributes}
              onChange={eventHandlers?.changePrefectureSelectBox}
            />
          </TableTrFormLabel>
          <TableTrFormLabel
            id="familyName"
            required={true}
            errorMessage={
              invalidErrors?.nameError?.reason
                ? invalidErrors?.nameError?.reason
                : ''
            }
            title="氏名"
          >
            <FormListName
              familyNameLabel="姓"
              givenNameLabel="名"
              placeholders={namePlaceholders}
              handleChangeFamilyName={eventHandlers?.changeFamilyName}
              handleChangeGivenName={eventHandlers?.changeGivenName}
            />
          </TableTrFormLabel>
          <TableTrFormLabel
            id="email"
            required={true}
            errorMessage={
              invalidErrors?.emailError?.reason
                ? invalidErrors?.emailError?.reason
                : ''
            }
            title="メールアドレス"
          >
            <FormListInputAndText
              text="ログイン時に必要になります。"
              NoteComponent={<EmailNote />}
            >
              <InputText
                name="email"
                id="email"
                type="email"
                width="default"
                placeholder="例)example@nurse-senka.jp"
                onChange={eventHandlers?.changeEmail}
              />
            </FormListInputAndText>
          </TableTrFormLabel>
          {isStudent ? (
            <TableTrFormLabel
              id="phoneNumber"
              required={true}
              errorMessage={
                invalidErrors?.phoneNumberError?.reason
                  ? invalidErrors?.phoneNumberError?.reason
                  : ''
              }
              title="電話番号"
            >
              <InputText
                name="phoneNumber"
                id="phoneNumber"
                type="tel"
                width="default"
                placeholder="例：09012345678"
                onChange={eventHandlers?.changePhoneNumber}
              />
            </TableTrFormLabel>
          ) : (
            ''
          )}

          <TableTrFormLabel
            id="password"
            required={true}
            errorMessage={
              invalidErrors?.passwordError?.reason
                ? invalidErrors?.passwordError?.reason
                : ''
            }
            title="パスワード"
          >
            <FormListPassword onChange={eventHandlers?.changePassword} />
          </TableTrFormLabel>
          {isStudent ? (
            <TableTrFormLabel
              id="registerNurseSenka"
              required={false}
              title="姉妹サイトへ登録"
            >
              <FormListInputAndText text="※看護学生・新卒看護師の就職活動をサポートするサイトです。合同説明会を開催するのはもちろん、病院検索や病院見学会・インターシップ・採用試験の申込み、無料で出来る適職診断など、役立つ就活情報をお届けします。">
                <ButtonText
                  color="default"
                  link={recruitNurseSenkaUrl(appEnv)}
                  useNextLink={true}
                  isTargetBlank={true}
                >
                  ナース専科 就職（看護学生・看護師の就活情報サイト）
                </ButtonText>
                に登録されます。
                <div>
                  <ButtonText
                    color="default"
                    link={`${recruitNurseSenkaUrl(appEnv)}/html/rule.html`}
                    useNextLink={true}
                    isTargetBlank={true}
                  >
                    ナース専科 就職 利用規約
                  </ButtonText>
                </div>
              </FormListInputAndText>
              <FormListInputAndText text="※ナース専科 就職では、あなたに興味を持った病院から直接お知らせメールが届く「スカウトメール」機能があります。効率的な就職活動のための情報収集にご活用いただけます。（チェックを外すと病院からメールが届かなくなります。）">
                <CheckBox
                  columns="default"
                  attributes={isReceiveHospitalNoticesAttribute}
                  onChange={eventHandlers?.changeIsReceiveHospitalNotices}
                  isUseChecked={isReceiveHospitalNotices}
                />
              </FormListInputAndText>
            </TableTrFormLabel>
          ) : (
            ''
          )}
        </TableBody>
        {errorMessage ? (
          <BlockErrorMessage>{errorMessage}</BlockErrorMessage>
        ) : (
          ''
        )}

        <InnerButtonArea>
          <InnerMargin align="center">
            <div>
              <ButtonText
                color="default"
                link={termsOfUseUrl(appEnv)}
                useNextLink={true}
              >
                利用規約
              </ButtonText>
              を確認・同意のうえ
            </div>
            ボタンをクリックしてください
          </InnerMargin>
          <ButtonBase
            color="primary"
            width="extend"
            disable={disableSubmitButton === true}
          >
            同意して登録する（無料）
          </ButtonBase>
        </InnerButtonArea>
      </form>
    </>
  );
};
